import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const ShippingZoneManager = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    pincode: '',
    area: '',
    city: '',
    rapidoRate: 0,
    porterRate: 0,
    rapidoDeliveryDays: 1,
    porterDeliveryDays: 3,
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Fetch all zones
  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/shipping/zones/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZones(response.data.data);
      setMessage('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching zones');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name.includes('Rate') || name.includes('Days') ? parseFloat(value) : value
    });
  };

  const handleAddZone = async (e) => {
    e.preventDefault();
    if (!formData.pincode || !formData.area || !formData.city || formData.rapidoRate === '' || formData.porterRate === '') {
      setError('All fields are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/shipping/zones`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setZones([...zones, response.data.data]);
      setFormData({
        pincode: '',
        area: '',
        city: '',
        rapidoRate: 0,
        porterRate: 0,
        rapidoDeliveryDays: 1,
        porterDeliveryDays: 3,
      });
      setMessage('Zone added successfully');
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding zone');
      setMessage('');
    }
  };

  const handleEditZone = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/api/shipping/zones/${editingId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setZones(zones.map(z => z.id === editingId ? response.data.data : z));
      setEditingId(null);
      setFormData({
        pincode: '',
        area: '',
        city: '',
        rapidoRate: 0,
        porterRate: 0,
        rapidoDeliveryDays: 1,
        porterDeliveryDays: 3,
      });
      setMessage('Zone updated successfully');
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating zone');
      setMessage('');
    }
  };

  const handleDeleteZone = async (id) => {
    if (!window.confirm('Are you sure you want to delete this zone?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/shipping/zones/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZones(zones.filter(z => z.id !== id));
      setMessage('Zone deleted successfully');
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting zone');
      setMessage('');
    }
  };

  const handleEditClick = (zone) => {
    setEditingId(zone.id);
    setFormData({
      pincode: zone.pincode,
      area: zone.area,
      city: zone.city,
      rapidoRate: zone.rapidoRate,
      porterRate: zone.porterRate,
      rapidoDeliveryDays: zone.rapidoDeliveryDays,
      porterDeliveryDays: zone.porterDeliveryDays,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      pincode: '',
      area: '',
      city: '',
      rapidoRate: 0,
      porterRate: 0,
      rapidoDeliveryDays: 1,
      porterDeliveryDays: 3,
    });
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-gray-900">🚚 Shipping Zone Manager</h2>

      {/* Form */}
      <form onSubmit={editingId ? handleEditZone : handleAddZone} className="mb-8 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          {editingId ? '✏️ Edit Zone' : '➕ Add New Zone'}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <input
            type="text"
            name="pincode"
            placeholder="Pincode (e.g., 500006)"
            value={formData.pincode}
            onChange={handleInputChange}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={editingId}
          />
          <input
            type="text"
            name="area"
            placeholder="Area (e.g., Gudimalkapur)"
            value={formData.area}
            onChange={handleInputChange}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            name="city"
            placeholder="City"
            value={formData.city}
            onChange={handleInputChange}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <input
            type="number"
            name="rapidoRate"
            placeholder="Rapido Rate (₹)"
            value={formData.rapidoRate}
            onChange={handleInputChange}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            step="0.01"
          />
          <input
            type="number"
            name="porterRate"
            placeholder="Porter Rate (₹)"
            value={formData.porterRate}
            onChange={handleInputChange}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            step="0.01"
          />
          <input
            type="number"
            name="rapidoDeliveryDays"
            placeholder="Rapido Days"
            value={formData.rapidoDeliveryDays}
            onChange={handleInputChange}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="1"
          />
          <input
            type="number"
            name="porterDeliveryDays"
            placeholder="Porter Days"
            value={formData.porterDeliveryDays}
            onChange={handleInputChange}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="1"
          />
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            {editingId ? '💾 Update Zone' : '➕ Add Zone'}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
            >
              ❌ Cancel
            </button>
          )}
        </div>
      </form>

      {/* Messages */}
      {message && <div className="p-4 mb-4 bg-green-100 text-green-700 rounded-lg">{message}</div>}
      {error && <div className="p-4 mb-4 bg-red-100 text-red-700 rounded-lg">{error}</div>}

      {/* Zones Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">📍 Pincode</th>
              <th className="px-4 py-2 text-left font-semibold">🏘️ Area</th>
              <th className="px-4 py-2 text-left font-semibold">🏙️ City</th>
              <th className="px-4 py-2 text-right font-semibold">🚗 Rapido Rate</th>
              <th className="px-4 py-2 text-right font-semibold">🚶 Porter Rate</th>
              <th className="px-4 py-2 text-center font-semibold">📅 Days</th>
              <th className="px-4 py-2 text-center font-semibold">⚙️ Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 py-4 text-center text-gray-500">Loading zones...</td>
              </tr>
            ) : zones.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-4 text-center text-gray-500">No shipping zones found</td>
              </tr>
            ) : (
              zones.map((zone) => (
                <tr key={zone.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 font-semibold text-blue-600">{zone.pincode}</td>
                  <td className="px-4 py-2">{zone.area}</td>
                  <td className="px-4 py-2">{zone.city}</td>
                  <td className="px-4 py-2 text-right">₹{parseFloat(zone.rapidoRate).toFixed(2)}</td>
                  <td className="px-4 py-2 text-right">₹{parseFloat(zone.porterRate).toFixed(2)}</td>
                  <td className="px-4 py-2 text-center">
                    🚗 {zone.rapidoDeliveryDays}d / 🚶 {zone.porterDeliveryDays}d
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => handleEditClick(zone)}
                      className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition mr-2"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteZone(zone.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded-lg text-sm text-gray-700">
        <p>💡 <strong>Hyderabad Base Location:</strong> 12-2-460/1/1,2 Mahek's Vijaya Vihar, Gudi Malkapur Road (500006)</p>
        <p>🚀 Manage shipping zones and rates for different pincodes in Hyderabad</p>
      </div>
    </div>
  );
};

export default ShippingZoneManager;
